import React, { useEffect, useState } from 'react';
import AdminNavbar from '../../component/adminNavbar';
import {
	Box,
	Heading,
	SimpleGrid,
	Text,
	Spinner,
	Center,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Badge,
	Flex,
	Card,
	CardBody,
	Icon,
	Button,
} from '@chakra-ui/react';
import {
	MdShoppingCart,
	MdAttachMoney,
	MdPendingActions,
	MdInventory,
	MdPeople,
	MdCancel,
} from 'react-icons/md';
import { DownloadIcon } from '@chakra-ui/icons';
import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

const exportCSV = (filename: string, headers: string[], rows: (string | number)[][]) => {
	const escape = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
	const csv = [headers.map(escape).join(','), ...rows.map((r) => r.map(escape).join(','))].join('\n');
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url; a.download = filename; a.click();
	URL.revokeObjectURL(url);
};

const authHeaders = () => ({
	headers: { 'x-auth-token': localStorage.getItem('x-auth-token') },
});

const fmt = (n: number) =>
	new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(n || 0);

const statusColor = (status: string) => {
	const s = status?.toLowerCase();
	if (s === 'completed') return 'green';
	if (s === 'pending') return 'yellow';
	if (s === 'canceled') return 'red';
	if (s === 'shipped') return 'blue';
	return 'gray';
};

const KpiCard = ({ icon, label, value, sub, color }) => (
	<Card shadow="sm" borderRadius="lg" bg="white">
		<CardBody>
			<Flex
				w="40px"
				h="40px"
				borderRadius="lg"
				bg={`${color}.50`}
				align="center"
				justify="center"
				mb={3}
			>
				<Icon as={icon} boxSize={5} color={`${color}.500`} />
			</Flex>
			<Text color="gray.500" fontSize="sm" mb={1}>{label}</Text>
			<Text fontSize="lg" fontWeight="bold" lineHeight="short" wordBreak="break-word">{value}</Text>
			{sub && <Text fontSize="xs" color="gray.400" mt={1}>{sub}</Text>}
		</CardBody>
	</Card>
);

const SectionCard = ({ title, children, loading, onExport }: { title: string; children: React.ReactNode; loading: boolean; onExport?: () => void }) => (
	<Card shadow="sm" borderRadius="lg" bg="white" overflow="hidden">
		<Box px={6} py={4} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
			<Flex justify="space-between" align="center">
				<Heading size="sm" color="gray.700">{title}</Heading>
				{onExport && (
					<Button size="xs" variant="outline" colorScheme="blue" leftIcon={<DownloadIcon />} onClick={onExport}>
						Export CSV
					</Button>
				)}
			</Flex>
		</Box>
		<CardBody p={0}>
			{loading ? (
				<Center py={10}><Spinner color="blue.500" /></Center>
			) : children}
		</CardBody>
	</Card>
);

const Reports = () => {
	const [summary, setSummary] = useState<any>(null);
	const [revenue, setRevenue] = useState<any[]>([]);
	const [statusData, setStatusData] = useState<any[]>([]);
	const [topProducts, setTopProducts] = useState<any[]>([]);
	const [facilityData, setFacilityData] = useState<any[]>([]);
	const [loadingSummary, setLoadingSummary] = useState(true);
	const [loadingRevenue, setLoadingRevenue] = useState(true);
	const [loadingStatus, setLoadingStatus] = useState(true);
	const [loadingProducts, setLoadingProducts] = useState(true);
	const [loadingFacility, setLoadingFacility] = useState(true);
	const [revByFacility, setRevByFacility] = useState<any[]>([]);
	const [loadingRevByFacility, setLoadingRevByFacility] = useState(true);

	useEffect(() => {
		const h = authHeaders();
		axios.get(`${BASE_URL}/api/reports/summary`, h).then((r) => setSummary(r.data)).finally(() => setLoadingSummary(false));
		axios.get(`${BASE_URL}/api/reports/revenue`, h).then((r) => setRevenue(r.data)).finally(() => setLoadingRevenue(false));
		axios.get(`${BASE_URL}/api/reports/order-status`, h).then((r) => setStatusData(r.data)).finally(() => setLoadingStatus(false));
		axios.get(`${BASE_URL}/api/reports/top-products`, h).then((r) => setTopProducts(r.data)).finally(() => setLoadingProducts(false));
		axios.get(`${BASE_URL}/api/reports/by-facility`, h).then((r) => setFacilityData(r.data)).finally(() => setLoadingFacility(false));
		axios.get(`${BASE_URL}/api/reports/revenue-by-facility`, h).then((r) => setRevByFacility(r.data)).finally(() => setLoadingRevByFacility(false));
	}, []);

	// Revenue bar chart: max bar width based on highest month
	const maxRevenue = Math.max(...revenue.map((r) => Number(r.revenue) || 0), 1);

	return (
		<>
			<AdminNavbar />
			<Box pt={{ base: 16, md: 6 }} px={{ base: 4, md: 6 }} pb={6} ml={{ base: 0, md: "250px" }} bg="gray.100" minH="100vh">
				<Flex justify="space-between" align="center" mb={2}>
					<Heading size="lg" color="gray.800">Reports</Heading>
					{summary && (
						<Button size="sm" variant="outline" colorScheme="blue" leftIcon={<DownloadIcon />}
							onClick={() => exportCSV("summary.csv",
								["Total Orders", "Total Revenue", "Pending Orders", "Canceled Orders", "Active Products", "Active Users"],
								[[summary.total_orders, summary.total_revenue, summary.pending_orders, summary.canceled_orders, summary.active_products, summary.active_users]]
							)}>
							Export Summary
						</Button>
					)}
				</Flex>
				<Text color="gray.500" fontSize="sm" mb={6}>Overview of sales, orders, and product performance</Text>

				{/* KPI Cards */}
				{loadingSummary ? (
					<Center py={10}><Spinner size="xl" color="blue.500" /></Center>
				) : (
					<SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4} mb={6}>
						<KpiCard icon={MdShoppingCart} label="Total Orders" value={summary?.total_orders ?? '—'} color="blue" />
						<KpiCard icon={MdAttachMoney} label="Total Revenue" value={fmt(summary?.total_revenue)} sub="Completed orders" color="green" />
						<KpiCard icon={MdPendingActions} label="Pending Orders" value={summary?.pending_orders ?? '—'} color="yellow" />
						<KpiCard icon={MdCancel} label="Canceled Orders" value={summary?.canceled_orders ?? '—'} color="red" />
						<KpiCard icon={MdInventory} label="Active Products" value={summary?.active_products ?? '—'} color="purple" />
						<KpiCard icon={MdPeople} label="Active Users" value={summary?.active_users ?? '—'} color="teal" />
					</SimpleGrid>
				)}

				<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} mb={4}>
					{/* Revenue by Month */}
					<SectionCard title="Revenue by Month (Last 12 Months)" loading={loadingRevenue} onExport={() => exportCSV("revenue-by-month.csv", ["Month", "Orders", "Revenue"], revenue.map((r) => [r.month, r.orders, r.revenue]))}>
						<Box px={6} py={4}>
							{revenue.length === 0 ? (
								<Text color="gray.400" fontSize="sm">No data available.</Text>
							) : (
								<Box overflowX="auto">
									<Table size="sm" variant="simple">
										<Thead>
											<Tr bg="gray.50">
												<Th>Month</Th>
												<Th isNumeric>Orders</Th>
												<Th isNumeric>Revenue</Th>
												<Th>Bar</Th>
											</Tr>
										</Thead>
										<Tbody>
											{revenue.map((row) => (
												<Tr key={row.month}>
													<Td fontWeight="medium">{row.month}</Td>
													<Td isNumeric>{row.orders}</Td>
													<Td isNumeric color="green.600" fontWeight="medium">{fmt(row.revenue)}</Td>
													<Td minW="100px">
														<Box
															h="10px"
															borderRadius="full"
															bg="blue.400"
															w={`${Math.round((Number(row.revenue) / maxRevenue) * 100)}%`}
															minW="4px"
														/>
													</Td>
												</Tr>
											))}
										</Tbody>
									</Table>
								</Box>
							)}
						</Box>
					</SectionCard>

					{/* Order Status Breakdown */}
					<SectionCard title="Orders by Status" loading={loadingStatus} onExport={() => exportCSV("orders-by-status.csv", ["Status", "Count", "Revenue"], statusData.map((r) => [r.status, r.count, r.revenue]))}>
						<Box px={6} py={4}>
							{statusData.length === 0 ? (
								<Text color="gray.400" fontSize="sm">No data available.</Text>
							) : (
								<Table size="sm" variant="simple">
									<Thead>
										<Tr bg="gray.50">
											<Th>Status</Th>
											<Th isNumeric>Count</Th>
											<Th isNumeric>Revenue</Th>
										</Tr>
									</Thead>
									<Tbody>
										{statusData.map((row) => (
											<Tr key={row.status}>
												<Td>
													<Badge colorScheme={statusColor(row.status)} px={2} py={1} borderRadius="md">
														{row.status}
													</Badge>
												</Td>
												<Td isNumeric fontWeight="medium">{row.count}</Td>
												<Td isNumeric color="green.600">{fmt(row.revenue)}</Td>
											</Tr>
										))}
									</Tbody>
								</Table>
							)}
						</Box>
					</SectionCard>
				</SimpleGrid>

				<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
					{/* Top Products */}
					<SectionCard title="Top 10 Products by Quantity Ordered" loading={loadingProducts} onExport={() => exportCSV("top-products.csv", ["Rank", "Product", "Qty", "Orders", "Revenue"], topProducts.map((r, i) => [i + 1, r.product_name, r.total_quantity, r.order_count, r.total_revenue]))}>
						<Box px={6} py={4}>
							{topProducts.length === 0 ? (
								<Text color="gray.400" fontSize="sm">No data available.</Text>
							) : (
								<Table size="sm" variant="simple">
									<Thead>
										<Tr bg="gray.50">
											<Th>#</Th>
											<Th>Product</Th>
											<Th isNumeric>Qty</Th>
											<Th isNumeric>Orders</Th>
											<Th isNumeric>Revenue</Th>
										</Tr>
									</Thead>
									<Tbody>
										{topProducts.map((row, i) => (
											<Tr key={row.product_name}>
												<Td color="gray.400" fontSize="xs">{i + 1}</Td>
												<Td fontWeight="medium" whiteSpace="normal">{row.product_name}</Td>
												<Td isNumeric>
													<Badge colorScheme="blue">{row.total_quantity}</Badge>
												</Td>
												<Td isNumeric>{row.order_count}</Td>
												<Td isNumeric color="green.600">{fmt(row.total_revenue)}</Td>
											</Tr>
										))}
									</Tbody>
								</Table>
							)}
						</Box>
					</SectionCard>

					{/* Orders by Facility */}
					<SectionCard title="Orders by Facility" loading={loadingFacility} onExport={() => exportCSV("orders-by-facility.csv", ["Rank", "Facility", "Orders", "Revenue"], facilityData.map((r, i) => [i + 1, r.facility, r.order_count, r.revenue]))}>
						<Box px={6} py={4}>
							{facilityData.length === 0 ? (
								<Text color="gray.400" fontSize="sm">No data available.</Text>
							) : (
								<Table size="sm" variant="simple">
									<Thead>
										<Tr bg="gray.50">
											<Th>#</Th>
											<Th>Facility</Th>
											<Th isNumeric>Orders</Th>
											<Th isNumeric>Revenue</Th>
										</Tr>
									</Thead>
									<Tbody>
										{facilityData.map((row, i) => (
											<Tr key={row.facility}>
												<Td color="gray.400" fontSize="xs">{i + 1}</Td>
												<Td fontWeight="medium" whiteSpace="normal">{row.facility}</Td>
												<Td isNumeric>
													<Badge colorScheme="purple">{row.order_count}</Badge>
												</Td>
												<Td isNumeric color="green.600">{fmt(row.revenue)}</Td>
											</Tr>
										))}
									</Tbody>
								</Table>
							)}
						</Box>
					</SectionCard>
				</SimpleGrid>

				{/* Revenue by Month per Facility */}
				<Box mt={4}>
					<SectionCard title="Revenue by Month per Facility (Last 12 Months)" loading={loadingRevByFacility}
						onExport={() => exportCSV("revenue-by-facility-month.csv",
							["Month", "Facility", "Orders", "Revenue"],
							revByFacility.map((r) => [r.month, r.facility, r.orders, r.revenue])
						)}>
						<Box px={6} py={4}>
							{revByFacility.length === 0 ? (
								<Text color="gray.400" fontSize="sm">No data available.</Text>
							) : (
								<Box overflowX="auto">
									<Table size="sm" variant="simple" minW="600px">
										<Thead>
											<Tr bg="gray.50">
												<Th>Month</Th>
												<Th>Facility</Th>
												<Th isNumeric>Orders</Th>
												<Th isNumeric>Revenue</Th>
											</Tr>
										</Thead>
										<Tbody>
											{revByFacility.map((row, i) => (
												<Tr key={i} _hover={{ bg: 'gray.50' }}>
													<Td fontWeight="medium">{row.month}</Td>
													<Td>{row.facility}</Td>
													<Td isNumeric>{row.orders}</Td>
													<Td isNumeric color="green.600" fontWeight="medium">{fmt(row.revenue)}</Td>
												</Tr>
											))}
										</Tbody>
									</Table>
								</Box>
							)}
						</Box>
					</SectionCard>
				</Box>
			</Box>
		</>
	);
};

export default Reports;
